import { Component, Injectable, OnInit, TemplateRef, ViewChild, Inject, HostListener, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

@Component({
    selector: 'app-dialog-main',
    templateUrl: './dialog-main.component.html',
    styleUrls: ['./dialog-main.component.scss'],
    standalone: false
})
export class DialogMainComponent implements OnInit, OnDestroy {
    @ViewChild('header', { static: true }) header: TemplateRef<any>;
    @ViewChild('body', { static: true }) body: TemplateRef<any>;
    @ViewChild('footer', { static: true }) footer: TemplateRef<any>;

    @ViewChild('deleteHeaderTemplate', { static: true }) deleteHeaderTemplate: TemplateRef<any>;
    @ViewChild('deleteFooterTemplate', { static: true }) deleteFooterTemplate: TemplateRef<any>;
    @ViewChild('confirmationFooterTemplate', { static: true }) confirmationFooterTemplate: TemplateRef<any>;

    @ViewChild('imageHeaderTemplate', { static: true }) imageHeaderTemplate: TemplateRef<any>;
    public footerTemplate: TemplateRef<any>;
    public headerTemplate: TemplateRef<any>;

    public disableConfirm = false;
    isLoadingEnable = false;
    ESCAPE_KEYCODE = 27 as const;
    BACKSPACE_KEYCODE = 8 as const;

    constructor(
        public dialogRef: MatDialogRef<any>,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        window.addEventListener('keydown', this.handleBackSpace.bind(this), false);
    }

    ngOnInit() {
        if (this.data?.type) {
            if (this.data.type === 'delete') {
                this.headerTemplate = this.deleteHeaderTemplate;
                this.footerTemplate = this.deleteFooterTemplate;
            } else if (this.data.type === 'image') {
                this.headerTemplate = this.imageHeaderTemplate;
                this.footerTemplate = this.footer;
            } else if (this.data.type === 'confirmation') {
                this.headerTemplate = this.deleteHeaderTemplate;
                this.footerTemplate = this.confirmationFooterTemplate;
            }
        } else {
            this.headerTemplate = this.header;
            this.footerTemplate = this.footer;
        }

        if (this.data.closeEvent) {
            this.data.closeEvent.subscribe(() => {
                this.dialogRef.close(false);
            });
        }

        this.dialogRef.afterClosed().subscribe(response => {
            this.onClose();
        });
    }

    /**
     *
     * handling the backspace event
     * @returns
     */
    private handleBackSpace(event) {
        if (event.keyCode !== this.BACKSPACE_KEYCODE) {
            return true;
        }

        // if backspace is pressed on specific class name then we will let it pass
        if (this.data?.ignoreClassName) {
            if (event.target.classList.contains(this.data?.ignoreClassName)) {
                return true;
            }
        }

        const acceptedInputTypes = [
            'text',
            'password',
            'number',
            'file',
            'search',
            'email',
            'date',
            'color',
            'datetime',
            'datetime-local',
            'month',
            'range',
            'tel',
            'time',
            'url',
            'week'
        ];
        const tagName = event?.target?.tagName?.toLowerCase() ?? '';
        const targetType = event?.target?.type?.toLowerCase() ?? '';
        //
        if ((tagName === 'input' && acceptedInputTypes.includes(targetType)) || tagName === 'textarea') {
            return true;
        } else {
            event.preventDefault();
            return false;
        }
    }

    @HostListener('document:keydown', ['$event']) onKeydownHandler(event: KeyboardEvent) {
        if (event.keyCode === this.ESCAPE_KEYCODE) {
            this.dialogRef.close();
        }
    }

    onClick(data) {
        if (this.data?.data) {
            this.dialogRef.close(this.data.data);
        } else {
            this.dialogRef.close(data);
        }
    }

    onConfirm() {
        if (this.data?.functions?.confirm) {
            this.isLoadingEnable = true;
            this.disableConfirm = true;
            this.data.functions.confirm(result => {
                this.isLoadingEnable = false;
                if (result) {
                    this.dialogRef.close();
                } else {
                    this.disableConfirm = false;
                }
            });
        }
    }

    onSave() {
        if (this.data?.functions?.save) {
            this.data.functions.save(result => {
                if (result) {
                    this.dialogRef.close();
                }
            });
        }
    }

    onUpdate() {
        if (this.data?.functions?.update) {
            this.data.functions.update(result => {
                if (result) {
                    this.dialogRef.close();
                }
            });
        }
    }

    onDelete() {
        if (this.data?.functions?.delete) {
            this.data.functions.delete(result => {
                if (result) {
                    this.dialogRef.close();
                }
            });
        }
    }

    onClear() {
        if (this.data?.functions?.clear) {
            this.data.functions.clear(result => {
                if (result) {
                    this.dialogRef.close();
                }
            });
        }
    }

    onCancel() {
        if (this.data?.functions?.cancel) {
            this.data.functions.cancel(result => {
                if (result) {
                    this.dialogRef.close();
                }
            });
        } else {
            this.dialogRef.close(false);
        }
    }

    onClose() {
        if (this.data?.functions?.close) {
            this.data.functions.close(result => {
                if (result) {
                    this.dialogRef.close();
                }
            });
        }
    }

    /** called when component is destroyed */
    public ngOnDestroy(): void {
        window.removeEventListener('keydown', this.handleBackSpace, false);
    }
}

@Injectable()
export class DialogRef extends MatDialog {}
